import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useFilters,
} from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "jalali-moment";
import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import { FaCircle, FaFileExcel, FaFilePdf, FaSearch } from "react-icons/fa";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { transactionEdit } from "../services/transactionServices";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import DateFilterBox from "./DateFilterBox";
import PDFGenerator from "../../../components/PDFGenerator";
import "../../../../assets/fonts/Vazir-normal";
import TransactionInfoModal from "./TransactionInfoModal";

const TransactionsTable = ({
  transactionList: data,
  fetchTransactionsList,
}) => {
  const [selectedMinDate, setSelectedMinDate] = useState(null);
  const [selectedMaxDate, setSelectedMaxDate] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("فیلتر بر اساس وضعیت");
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [transactionModalData, setTransactionModalData] = useState(null);
  const [lastCount, setLastCount] = useState("");
  const accessToken = useSelector((state) => state.auth.accessToken);
  const STATUS_OPTIONS = [
    { value: "success", label: "پرداخت موفق" },
    { value: "fail", label: "پرداخت ناموفق" },
    { value: "pending", label: "در حال پرداخت" },
    { value: "cancel", label: "انصراف" },
  ];
  const sortedData = useMemo(() => {
    return [...data].sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
    );
  }, [data]);
  const openModal = (row) => {
    setTransactionModalData(row);
    setIsTransactionModalOpen(true);
  };

  const closeModal = () => {
    setIsTransactionModalOpen(false);
    setTransactionModalData(null);
  };
  const columns = useMemo(
    () => [
      {
        Header: "#",
        Cell: ({ row }) => row.index + 1,
        disableSortBy: true,
      },
      {
        Header: "نام بات",
        accessor: (row) => row?.business?.username,
      },
      {
        Header: " علت پرداخت",
        accessor: "subId",
        Cell: ({ value }) => (
          <div className="text-center whitespace-nowrap">
            {value && value !== "0" && value !== null && value !== undefined
              ? "خرید طرح"
              : "افزایش اعتبار"}
          </div>
        ),
      },
      {
        Header: "تاریخ پرداخت",
        accessor: "createdAt",
        Cell: ({ value }) =>moment.utc(value)
        .local()
        .locale('fa')
        .format("jYYYY/jMM/jDD HH:mm"),
      },
      {
        Header: "مبلغ(ریال)",
        accessor: (row) => row?.paidAmount.toLocaleString("fa-IR"),
      },
      {
        Header: "درگاه",
        accessor: "ipg",
        Cell: ({ value }) => (
          <div className="text-center whitespace-nowrap">
            {value == "zibal"
              ? "زیبال"
              : value == "zarinpal"
              ? "زرین پال"
              : value == "wallet"
              ? "کسر از اعتبار"
              : "---"}
          </div>
        ),
      },
      //  {
      //     Header: "کد پیگیری",
      //     accessor: "trackId",
      //     Cell: ({ value }) => (
      //         <div className="text-center">
      //             {value && value !== "0" && value !== null && value !== undefined ? `${value}` : "----"}
      //         </div>
      //     ),

      // }
      {
        Header: "اطلاعات پرداخت",
        // accessor: "trackId",
        Cell: ({ row }) => (
          <div className="text-center">
            <button
              onClick={() => openModal(row.original)}
              className="text-blue-500 hover:text-blue-700 transition"
              title="مشاهده اطلاعات پرداخت"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 inline"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
          </div>
        ),
      },
      ,
      {
        Header: "وضعیت",
        accessor: "status",
        Cell: ({ row }) => {
          const value = row.original.status;
          const handleChange = useCallback(
            async (e) => {
              const newStatus = e.target.value;
              const transactionId = String(row.original.id);

              try {
                const res = await transactionEdit({
                  accessToken,
                  id: transactionId,
                  status: newStatus,
                });
                if (res?.ok) {
                  toast.success("وضعیت با موفقیت به‌روزرسانی شد");
                  await fetchTransactionsList();
                }
              } catch (err) {
                toast.error("خطا در بروزرسانی وضعیت");
              }
            },
            [accessToken, row.original.id, fetchTransactionsList]
          );

          return (
            <select
              className={`text-white border-0 ${
                value == "fail"
                  ? "bg-rose-500"
                  : value == "success"
                  ? "bg-green-500"
                  : value == "pending"
                  ? "bg-orange-400"
                  : "bg-yellow-500"
              } rounded px-7 py-2 text-sm`}
              value={value}
              onChange={handleChange}
            >
              {STATUS_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  className="bg-gray-100 text-white"
                >
                  {option.label}
                </option>
              ))}
            </select>
          );
        },
      },
    ],
    [accessToken, fetchTransactionsList]
  );
  useEffect(() => {
    if (selectedMinDate) {
      const m = moment
        .from(
          `${selectedMinDate.year}/${selectedMinDate.month}/${selectedMinDate.day}`,
          "fa",
          "YYYY/MM/DD"
        )
        .startOf("day");
      setMinDate(m.toISOString());
    } else {
      setMinDate("");
    }

    if (selectedMaxDate) {
      const m = moment
        .from(
          `${selectedMaxDate.year}/${selectedMaxDate.month}/${selectedMaxDate.day}`,
          "fa",
          "YYYY/MM/DD"
        )
        .endOf("day");
      setMaxDate(m.toISOString());
    } else {
      setMaxDate("");
    }
  }, [selectedMinDate, selectedMaxDate]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(sortedData)) return [];

    const result = sortedData.filter((item) => {
      const createdDate = item.createdAt ? moment(item.createdAt) : null;

      const matchesDate =
        (!minDate ||
          (createdDate && createdDate.isSameOrAfter(moment(minDate)))) &&
        (!maxDate ||
          (createdDate && createdDate.isSameOrBefore(moment(maxDate))));

      const matchesName =
        item?.business?.username
          ?.toLowerCase()
          ?.includes(globalFilter?.toLowerCase()) ||
        item?.businessId
          ?.toLowerCase()
          ?.includes(globalFilter?.toLowerCase()) ||
        item?.trackId?.toString()?.includes(globalFilter);

      const matchesStatus =
        statusFilter === "" ||
        (statusFilter === "success" && item.status === "success") ||
        (statusFilter === "fail" && item.status === "fail") ||
        (statusFilter === "pending" && item.status === "pending") ||
        (statusFilter === "cancel" && item.status === "cancel");

      return matchesName && matchesDate && matchesStatus;
    });

    // return lastCount ? result.slice(-lastCount) : result;
    return lastCount ? result.slice(0, Number(lastCount)) : result;
  }, [sortedData, globalFilter, minDate, maxDate, statusFilter, lastCount]);
  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: 5 },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = tableInstance;

  const exportToExcel = () => {
    const mappedData = filteredData.map((item) => ({
      "کد تراکنش": item.id,
      مبلغ: Number(item.paidAmount).toLocaleString("en-US"), // 👈🏻 تبدیل مبلغ با کاما
      تاریخ: moment.utc(item.createdAt)
      .local()
      .locale('fa')
      .format("jYYYY/jMM/jDD HH:mm"),
    }));

    const ws = XLSX.utils.json_to_sheet(mappedData, { origin: "A2" });

    // ساخت تیتر بالای جدول
    XLSX.utils.sheet_add_aoa(ws, [["لیست تراکنش‌ها"]], { origin: "A1" });

    // Merge تیتر روی سه ستون
    ws["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: 2 },
      },
    ];

    // تعیین عرض ستون‌ها
    ws["!cols"] = [
      { wch: 40 }, // کد تراکنش
      { wch: 20 }, // مبلغ (یکم بیشتر چون با کاما شده)
      { wch: 25 }, // تاریخ
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "transactionList");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(dataBlob, "transactionList.xlsx");
  };
  const exportToPDF = () => {
    const doc = new jsPDF();

    // doc.addFileToVFS("Vazir.ttf", vazirFont);
    // doc.addFont("Vazir.ttf", "Vazir", "normal");
    doc.setFont("Vazir");
    const tableColumn = ["کد تراکنش", "مبلغ", "تاریخ"];
    const tableRows = filteredData.map((item) => [
      item.id,
      item.paidAmount,
      moment.utc(item.createdAt)
            .local()
            .locale('fa')
            .format("jYYYY/jMM/jDD HH:mm"),,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: {
        font: "Vazir",
        fontStyle: "normal",
        halign: "center",
      },
      headStyles: {
        halign: "center",
      },
    });
    doc.save("transactionList.pdf");
  };

  const resetFilters = () => {
    setGlobalFilter("");
    setStatusFilter("");
    setSelectedStatus("فیلتر بر اساس وضعیت");
    setSelectedMinDate(null);
    setSelectedMaxDate(null);
    setLastCount("");
    tableInstance.state.pageIndex = 0;
  };
  return (
    <div className="p-4  max-w-5xl mx-auto">
      <div className="space-y-4 mb-5 mt-5">
        <div className="flex flex-wrap gap-4 items-center">
          <label
            htmlFor="search"
            className="text-gray-700 font-semibold text-lg"
          >
            جستجو
          </label>
          <div className="relative w-full md:w-1/2">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            <input
              id="search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="جستجوی نام بات،کد پیگیری،..."
              className="border rounded px-4 py-2 pl-10 w-full text-sm focus:border-[#aadec5] focus:outline-none focus:ring-2 focus:ring-[#aadec5]"
            />
          </div>
        </div>

        {/* سطر دوم: فیلترها و دکمه‌ها */}
        <div className="flex flex-wrap gap-8 items-end flex-grow">
          <div className="flex flex-wrap sm:flex-nowrap gap-4">
            <div className="flex flex-col">
              <Menu
                as="div"
                className="relative inline-block text-left w-[200px]"
              >
                <div>
                  <Menu.Button
                    className="inline-flex w-full justify-between gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs
                                     ring-1 ring-gray-500 ring-inset hover:bg-gray-50
                                     "
                  >
                    {selectedStatus}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="-mr-1 size-5 text-gray-900"
                    />
                  </Menu.Button>
                </div>

                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y  divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          onClick={() => {
                            setSelectedStatus("پرداخت موفق");
                            setStatusFilter("success");
                          }}
                          className={`block px-4 py-2 text-center text-sm text-gray-900 ${
                            active ? "bg-gray-100 text-gray-900" : ""
                          }`}
                        >
                          پرداخت موفق
                        </a>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          onClick={() => {
                            setSelectedStatus("پرداخت ناموفق");
                            setStatusFilter("fail");
                          }}
                          className={`block px-4 py-2 text-center text-sm text-gray-700 ${
                            active ? "bg-gray-100 text-gray-900" : ""
                          }`}
                        >
                          پرداخت ناموفق
                        </a>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          onClick={() => {
                            setSelectedStatus("در حال پرداخت");
                            setStatusFilter("pending");
                          }}
                          className={`block px-4 py-2 text-center text-sm text-gray-700 ${
                            active ? "bg-gray-100 text-gray-900" : ""
                          }`}
                        >
                          در حال پرداخت
                        </a>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          onClick={() => {
                            setSelectedStatus("انصراف");
                            setStatusFilter("cancel");
                          }}
                          className={`block px-4 py-2 text-center text-sm text-gray-700 ${
                            active ? "bg-gray-100 text-gray-900" : ""
                          }`}
                        >
                          انصراف
                        </a>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Menu>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 items-center ">
            <div className="flex flex-wrap sm:flex-nowrap gap-4">
              <select
                value={lastCount}
                onChange={(e) => setLastCount(Number(e.target.value))}
                className="border px-3 py-2 rounded text-sm w-[200px]"
              >
                <option value=""> تمامی تراکنش ها</option>
                <option value="10">۱۰ تا گردش آخر</option>
                <option value="20">۲۰ تا گردش آخر</option>
                <option value="50">۵۰ تا گردش آخر</option>
                <option value="100">۱۰۰ تا گردش آخر</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap sm:flex-nowrap gap-4">
            {/* تاریخ‌ها */}
            <DateFilterBox
              selectedMinDate={selectedMinDate}
              setSelectedMinDate={setSelectedMinDate}
              selectedMaxDate={selectedMaxDate}
              setSelectedMaxDate={setSelectedMaxDate}
              onClearDates={() => {
                setSelectedMinDate(null);
                setSelectedMaxDate(null);
              }}
            />
          </div>
          {/* دکمه‌ها */}
          <button
            onClick={resetFilters}
            className="bg-yellow-400 px-4 py-2 rounded text-white h-9 mr-auto"
          >
            حذف فیلترها
          </button>
        </div>
      </div>

      {/* 
            <div className="overflow-x-auto">
                <table {...getTableProps()} className="min-w-full bg-white border border-gray-300  rounded-xl shadow-md text-sm sm:text-base">
                    <thead className="bg-gray-100">
                        {headerGroups.map((headerGroup) => (
                            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        key={column.id}
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className="text-center px-3  py-2 border-b border-gray-300 cursor-pointer whitespace-nowrap"
                                    >
                                        {column.render("Header")}
                                        <span>
                                            {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);
                            return (
                                <tr key={row.id || row.original.id} {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td key={cell.column.id} {...cell.getCellProps()} className="px-3 py-2 border-b text-center">
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div> */}
      <div className="overflow-x-auto">
        <table
          {...getTableProps()}
          className="min-w-full bg-white border border-gray-300 rounded-xl shadow-md text-sm sm:text-base"
        >
          <thead className="bg-gray-100">
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroup.id} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key, ...restHeaderProps } = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );
                    return (
                      <th
                        key={column.id}
                        {...restHeaderProps}
                        className="text-center px-3 py-2 border-b border-gray-300 cursor-pointer whitespace-nowrap"
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key, ...restRowProps } = row.getRowProps();
              return (
                <tr key={row.id || row.original.id} {...restRowProps}>
                  {row.cells.map((cell) => {
                    const { key, ...restCellProps } = cell.getCellProps();
                    return (
                      <td
                        key={cell.column.id}
                        {...restCellProps}
                        className="px-3 py-2 border-b text-center"
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2 items-end justify-end mt-5">
        <button
          onClick={exportToExcel}
          className="bg-green-500 px-4 py-2 rounded text-white"
        >
          <FaFileExcel />
        </button>
        <button
          onClick={exportToPDF}
          className="bg-red-500 px-4 py-2 rounded text-white"
        >
          <FaFilePdf />
          {/* <PDFGenerator
                        headers={headers}
                        data={dataPdf}
                        fileName="transactions.pdf"
                        title="گزارش تراکنش‌ها"
                    /> */}
        </button>
      </div>
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span>تعداد ردیف در صفحه:</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border rounded  py-2"
          >
            {[5, 10, 20].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={previousPage}
            disabled={!canPreviousPage}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            قبلی
          </button>
          <button
            onClick={nextPage}
            disabled={!canNextPage}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            بعدی
          </button>
        </div>
      </div>
      <TransactionInfoModal
        isOpen={isTransactionModalOpen}
        closeModal={closeModal}
        modalData={transactionModalData}
      />
    </div>
  );
};

export default TransactionsTable;
